import { Component, EventEmitter, inject, OnDestroy, Output, signal } from '@angular/core';
import { Filter } from 'src/app/models/filter';
import { Subscription } from 'rxjs';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyRadioChange as MatRadioChange } from '@angular/material/legacy-radio';
import { ActivatedRoute, Router } from '@angular/router';
import { FilterValue } from 'src/app/models/filter-value';
import { ViewAllValuesDialogComponent } from '../view-all-values-dialog/view-all-values-dialog.component';
import { SearchModel, SearchState } from 'src/app/models/search-model';
import { ResizeService } from 'src/app/services/resize.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ConfigService } from 'src/app/services/config.service';
import { AvailabilityFilter } from 'src/app/state/user/searches/models/search';
import { Util } from 'src/app/util';
import { Icon } from 'src/app/models/record';
import { ThemaService } from '../../services/thema.service';
import { LocaleService } from '../../services/locale.service';
import { ViewAllValuesDialogData } from 'src/app/models/user-item-list';
import { LearningExperienceInfo } from 'src/app/models/experiences';
import { ApiService } from '../../services/api.service';
import { InfoDialogFacade } from 'src/app/_core/state/dialogs/info/info.facade';
import { AnalyticsService } from 'src/app/_core/analytics/analytics.service';

const AVAILABILITY_FILTER = 'availability_facet_ss';
const LISTS_FILTER = 'listas_ls';

@Component({
  selector: 'app-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  animations: [
    trigger('expandAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate(300)]),
      transition(':leave', [animate(300, style({ opacity: 0 }))]),
      state('*', style({ opacity: 1 })),
    ]),
  ],
})
export class FiltersComponent implements OnDestroy {
  readonly MAX_FILTER_VALUES = Util.MAX_FILTER_VALUES;

  private readonly analyticsService = inject(AnalyticsService);
  private readonly infoDialogFacade = inject(InfoDialogFacade);

  readonly isFilterListDialogOpen = signal<boolean>(false);
  readonly selectedFilterName = signal<string>('');
  readonly filterListParams = signal<Record<string, string | boolean | number>>({});

  @Output()
  submitted: EventEmitter<boolean> = new EventEmitter();

  filters: Filter[] = [];
  formatFilter: Filter = this.getEmptyFormatFilter();
  availabilityFilter: AvailabilityFilter;
  selectedAvailability: string;
  isMobile: boolean;
  formatFilterInfoMap = new Map<FilterValue, Icon>();
  traditionalFormatFilter: Filter;
  experiencesFormatFilter: Filter;
  physicalFilterKeys: Filter;
  availableExperiencesInfo: LearningExperienceInfo[];
  showExperiencesFeatures: boolean;
  showFormatSeparator: boolean;
  showPhysicalFormatSeparator: boolean;

  searchModel: SearchModel;
  sidebarSub = new Subscription();
  searchModelSub = new Subscription();
  filtersSub = new Subscription();

  showAvailabilityFilter: boolean;
  isSideBarOpen = false;

  constructor(
    private readonly apiService: ApiService,
    private readonly route: ActivatedRoute,
    private broadcastService: BroadcastService,
    private resizeService: ResizeService,
    private configService: ConfigService,
    private themaService: ThemaService,
    private localeService: LocaleService,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.showExperiencesFeatures = this.configService.get().showExperiencesFeatures;
    this.isMobile = this.resizeService.isMobile;
    this.sidebarSub = this.broadcastService.sidebar$.subscribe((value) => {
      this.isSideBarOpen = value;
    });
    this.searchModel = new SearchModel(null, this.configService.get().resultsFilterOrder);
    this.searchModelSub = this.broadcastService.searchModel$.subscribe((model) => (this.searchModel = model));

    this.traditionalFormatFilter = this.getEmptyFormatFilter();
    this.experiencesFormatFilter = this.getEmptyFormatFilter();
    this.physicalFilterKeys = this.getEmptyFormatFilter();

    this.filtersSub = this.broadcastService.filters$.subscribe((filters) => {
      if (!filters) {
        filters = [];
      }

      if (filters.length) {
        this.traditionalFormatFilter.values = [];
        this.experiencesFormatFilter.values = [];
        this.physicalFilterKeys.values = [];

        this.filters = filters.filter(
          (filter) => ![AVAILABILITY_FILTER, Util.FORMAT_FILTER, LISTS_FILTER].includes(filter.name)
        );
        this.formatFilter = filters.find((filter) => filter.name === Util.FORMAT_FILTER); // mobile view
        if (this.formatFilterInfoMap && this.formatFilter) {
          Util.fillFormatFilterInfoMap(this.formatFilterInfoMap, this.formatFilter);
          this.formatFilter.values.forEach((elem) => {
            if (Util.isLearningExperienceFilterValue(elem)) {
              this.experiencesFormatFilter.values.push(elem);
            } else if (Util.isPhysicalFilterValue(elem)) {
              this.physicalFilterKeys.values.push(elem);
            } else {
              this.traditionalFormatFilter.values.push(elem);
            }
          });
        }

        this.showExperiencesFeatures =
          this.configService.get().showExperiencesFeatures && this.experiencesFormatFilter.values.length > 0;
        this.showFormatSeparator =
          this.traditionalFormatFilter.values.length > 0 &&
          (this.showExperiencesFeatures || this.physicalFilterKeys.values.length > 0);
        this.showPhysicalFormatSeparator = this.physicalFilterKeys.values.length > 0 && this.showExperiencesFeatures;

        const availabilityFilter = filters.find((filter) => filter.name === AVAILABILITY_FILTER);

        if (availabilityFilter) {
          this.availabilityFilter = new AvailabilityFilter(availabilityFilter);
          if (this.availabilityFilter && this.availabilityFilter.total) {
            this.selectedAvailability = this.availabilityFilter.total.field;
          }
        }

        this.filters.forEach((filter) => {
          filter.action = 'common.viewMore';
          filter.icon = 'add';
        });

        this.markSelectedFilters();
      } else {
        this.filters = [];
        this.availabilityFilter = null;
      }

      this.showAvailabilityFilter = this.shouldShowAvailability();
    });
  }

  ngOnDestroy() {
    this.sidebarSub.unsubscribe();
    this.filtersSub.unsubscribe();
    this.searchModelSub.unsubscribe();
  }

  markSelectedFilters() {
    this.searchModel.filters.forEach((filter) => {
      if (filter.name === AVAILABILITY_FILTER) {
        this.selectedAvailability = filter.value as string;
      } else {
        this.filters.forEach((f) => {
          f.values.forEach((v) => {
            if (
              (f.name === filter.name && v.name === filter.value) ||
              (this.filters.some((filterValue) => filterValue.name === 'biblioHO') && filter.value === v.name)
            ) {
              v.selected = true;
            }
          });
        });

        this.formatFilter?.values.forEach((ff) => {
          if (ff.name === filter.value) {
            ff.selected = true;
          }
        });
      }
    });
  }

  onAvailabilityChange(event: MatRadioChange) {
    this.applyFilter(AVAILABILITY_FILTER, event.value, true);
  }

  applyFilter(name: string, value: string, single?: boolean) {
    this.submitted.emit(true);
    const state: SearchState = { ...new SearchState(), searchModel: this.searchModel };
    this.searchModel.applyFilter({ name: name, value: value, single: single });
    this.searchModel.save = true;

    this.analyticsService.searchPerformedOrigin = 'filter_bar';

    this.router.navigate(['/results'], {
      queryParams: this.searchModel.toRequest(state),
    });
  }
  /**
   * Apply filter, considering it might need codeBiblio/nameBiblio
   * @param name
   * @param filterValue
   * @param single
   */
  applyHoFilter(name: string, filterValue: FilterValue, single?: boolean) {
    if (filterValue.nameBiblio !== undefined) {
      this.applyFilter('biblioHO', filterValue.name, single);
    } else {
      const value: string = filterValue.name;
      this.applyFilter(name, value, single);
    }
  }

  getExpandedValues(values: FilterValue[]): FilterValue[] {
    return values.slice(0, Util.MAX_FILTER_VALUES);
  }

  getFilterExpandedValues(filter: Filter): FilterValue[] {
    if (filter.name === 'biblioName' && this.biblioHoInFilters()) {
      return this.getExpandedValues(this.filters.find((f) => f.name === 'biblioHO').values);
    }
    return this.getExpandedValues(filter.values);
  }

  getCollapsedValues(values: FilterValue[]): FilterValue[] {
    return values.slice(Util.MAX_FILTER_VALUES, Util.MAX_EXPANDED_VALUES);
  }

  getFilterCollapsedValues(filter: Filter): FilterValue[] {
    if (filter.name === 'biblioName' && this.biblioHoInFilters()) {
      return this.getCollapsedValues(this.filters.find((f) => f.name === 'biblioHO').values);
    }
    return this.getCollapsedValues(filter.values);
  }

  toggleValuesPanel(filter: Filter) {
    filter.expanded = !filter.expanded;
    filter.action = filter.expanded ? 'common.viewLess' : 'common.viewMore';
    filter.icon = filter.expanded ? 'remove' : 'add';
  }

  hasViewMore(values: FilterValue[]): boolean {
    return values.length > Util.MAX_FILTER_VALUES;
  }

  hasViewMoreFilter(filter: Filter): boolean {
    if (filter.name === 'biblioName' && this.biblioHoInFilters()) {
      return this.hasViewMore(this.filters.find((f) => f.name === 'biblioHO').values);
    }
    return this.hasViewMore(filter.values);
  }

  shouldShowViewAll(filter: Filter): boolean {
    return (filter.expanded && filter.values.length > Util.MAX_EXPANDED_VALUES) || this.isBiblioHoExpanded(filter);
  }

  shouldShowViewAllFilter(filter: Filter): boolean {
    if (filter.name === 'biblioName' && this.biblioHoInFilters()) {
      return this.shouldShowViewAll(this.filters.find((f) => f.name === 'biblioHO'));
    }
    return this.shouldShowViewAll(filter);
  }

  adaptAndTranslate(filter: Filter, value: FilterValue): string {
    if (Util.PPU_FILTER.includes(filter.name)) {
      return this.localeService.instant(`info.${value.name}`, {
        default: value.name,
      });
    } else if (Util.THEMA_FILTER.includes(filter.name)) {
      return this.themaService.getDescription(value.name);
    } else if (value.nameToShow) {
      return this.localeService.instant(value.nameToShow);
    } else if (value.name) {
      return this.localeService.instant(value.name);
    } else if (filter.name === 'biblioName' && value.nameToShow) {
      return value.nameToShow;
    }
    return '';
  }

  getFormatInfo(format: string, iconId?: string): Icon {
    return Util.resolveIcon(format, iconId);
  }

  openViewAllDialogFilter(filter: Filter) {
    if (filter.name === 'biblioName' && this.biblioHoInFilters()) {
      const biblioHoFilter = this.filters.find((f) => f.name === 'biblioHO');
      biblioHoFilter.label = this.filters.find((f) => f.name === 'biblioName').label;
      this.openViewAllDialog(biblioHoFilter);
    } else {
      this.openViewAllDialog(filter);
    }
  }

  openViewAllDialog(filter: Filter) {
    const params: Record<string, string | boolean | number> = {
      locale: this.localeService.language,
      excludeNonLeRecords: this.searchModel.excludeNonLeRecords,
    };

    const query = this.searchModel.getQuery();
    const facets = this.searchModel.getFacets();

    if (query) {
      params.query = query;
    }

    if (facets) {
      params.facets = facets;
    }

    if (this.searchModel.collectionId) {
      params.collectionId = this.searchModel.collectionId;
    }

    if (this.searchModel.challengeId) {
      params.challengeId = this.searchModel.challengeId;
    }

    if (filter.name !== 'thema_facet_ss') {
      this.selectedFilterName.set(filter.name);
      this.filterListParams.set(params);
      this.isFilterListDialogOpen.set(true);
      return;
    }

    this.apiService.getFilter(filter.name, params).subscribe((filterResponse) => {
      const data: ViewAllValuesDialogData = {
        filter: filterResponse,
      };
      this.dialog
        .open(ViewAllValuesDialogComponent, {
          data: data,
        })
        .afterClosed()
        .subscribe((value) => {
          if (value) {
            if (filterResponse.name === 'biblioHO') {
              this.applyFilter(filterResponse.name, value.name);
            } else {
              this.applyFilter(filterResponse.name, value.name);
            }
          }
        });
    });
  }

  private getEmptyFormatFilter() {
    const emptyFormatFilter: Filter = {
      label: '',
      name: Util.FORMAT_FILTER,
      action: 'common.viewMore',
      icon: 'add',
      values: [],
    };
    return emptyFormatFilter;
  }

  private shouldShowAvailability(): boolean {
    const hasFilter = this.availabilityFilter;
    const hasKeys = Object.keys(this.availabilityFilter || {}).length === 2;

    return this.configService.get().showAvailabilityFilter && hasFilter && hasKeys;
  }

  openExperiencesInfoDialog() {
    this.infoDialogFacade.openDialog(
      this.configService.getAvailableExperiencesInfoDialogData().title,
      this.configService.getAvailableExperiencesInfoDialogData().intro,
      this.configService.getAvailableExperiencesInfoDialogData().items
    );
  }

  /**
   * If biblioHo is present, we only want its values, not to show it twice.
   * @param filter
   */
  isBiblioHoFilter(filter: Filter): boolean {
    return filter.name === 'biblioHO';
  }

  biblioHoInFilters(): boolean {
    return this.filters.some((filter) => this.isBiblioHoFilter(filter));
  }

  private isBiblioHoExpanded(filter: Filter): boolean {
    return this.isBiblioHoFilter(filter) && this.filters.find((f) => f.name === 'biblioName').expanded;
  }

  applyFilterFromList(filter) {
    if (filter) {
      const { name, value } = filter;
      this.applyFilter(name, value);
    }

    this.isFilterListDialogOpen.set(false);
    this.selectedFilterName.set('');
    this.filterListParams.set({});
  }
}
